import { Combobox as ComboboxPrimitive } from '@base-ui/react/combobox';
import type { ComponentPropsWithRef, Ref } from 'react';
import { useCallback } from 'react';

import { Spinner } from '@/components/loaders';
import { cn } from '@/lib/utils';

import { useComboboxContext } from './ComboboxContext';

const pickerControlChipsContainerSizeClasses = {
  small: 'min-h-6',
  medium: 'min-h-8',
  large: 'min-h-10',
};

const comboboxControlSizeClasses = {
  small: 'py-px text-sm',
  medium: 'py-[3px] text-sm',
  large: 'py-[5px] text-base',
};

const chipGapSizeClasses = {
  small: 'gap-1',
  medium: 'gap-1',
  large: 'gap-1',
};

const inputGroupButtonSizeClasses = {
  small: '[&_svg]:size-4.5',
  medium: '[&_svg]:size-5',
  large: '[&_svg]:size-6',
};

const setRef = <T,>(ref: Ref<T> | undefined, value: T | null) => {
  if (typeof ref === 'function') ref(value);
  else if (ref) (ref as { current: T | null }).current = value;
};

export type ComboboxChipsProps = ComponentPropsWithRef<typeof ComboboxPrimitive.Chips> &
  ComboboxPrimitive.Chips.Props;

const ComboboxChips = ({
  className,
  ref: refProp,
  children,
  'aria-invalid': ariaInvalidFromProps,
  ...props
}: ComboboxChipsProps) => {
  const { size, isLoading, setChipsAnchor } = useComboboxContext();
  const ariaInvalid = ariaInvalidFromProps;

  const ref = useCallback(
    (el: HTMLDivElement | null) => {
      setChipsAnchor(el);
      setRef(refProp, el);
    },
    [refProp, setChipsAnchor],
  );

  return (
    <ComboboxPrimitive.Chips
      ref={ref}
      data-slot="combobox-chips"
      className={cn(
        'group relative flex flex-wrap items-center rounded-xs border border-gray-600 bg-white bg-clip-padding transition-colors outline-none',
        'px-3 has-data-[slot=combobox-chip]:px-1',

        'hover:border-gray-900 hover:focus-within:border-green-500',

        'focus-within:border-ring focus-within:ring-1 focus-within:ring-gray-200/50',
        'focus-within:border-green-500 focus-within:ring-1 focus-within:ring-green-500/50',

        'has-data-warning:border-yellow-400 has-data-warning:focus-within:border-yellow-500 has-data-warning:focus-within:ring-yellow-500/50 has-data-warning:hover:border-yellow-500',
        'has-aria-invalid:border-red-400 has-aria-invalid:focus-within:border-red-500 has-aria-invalid:focus-within:ring-red-500/50 has-aria-invalid:hover:border-red-500',

        'data-warning:border-yellow-400 data-warning:focus-within:border-yellow-500 data-warning:focus-within:ring-yellow-500/50 data-warning:hover:border-yellow-500',
        'aria-invalid:border-red-400 aria-invalid:focus-within:border-red-500 aria-invalid:focus-within:ring-red-500/50 aria-invalid:hover:border-red-500',

        'has-disabled:cursor-not-allowed has-disabled:border-gray-400 has-disabled:bg-gray-100 has-disabled:text-gray-400 has-disabled:hover:border-gray-400!',
        comboboxControlSizeClasses[size],
        pickerControlChipsContainerSizeClasses[size],
        chipGapSizeClasses[size],
        className,
      )}
      {...props}
      aria-invalid={ariaInvalid}
    >
      {children}
      {isLoading && (
        <div
          className={cn(
            'absolute right-2 group-has-data-[slot=combobox-chip]:hidden',
            inputGroupButtonSizeClasses[size],
          )}
        >
          <Spinner className="pointer-events-none" />
        </div>
      )}
    </ComboboxPrimitive.Chips>
  );
};

export default ComboboxChips;
